<template>
  <div>
    <div class="filter-bar">
      <el-input
        v-model="search"
        size="small"
        clearable
        prefix-icon="el-icon-search"
        placeholder="Search node ID, friendly name or model"
        style="width:260px"
      />
      <el-select
        v-model="status"
        size="small"
        clearable
        placeholder="All Statuses"
        style="width:160px"
        @change="loadNodes"
        ><el-option
          v-for="item in statuses"
          :key="item"
          :label="item"
          :value="item"
      /></el-select>
      <el-button size="small" icon="el-icon-refresh" @click="loadNodes"
        >Refresh</el-button
      >
      <span class="result-count">{{ filteredNodes.length }} Minew Nodes</span>
      <el-button
        v-if="$permission('Installation Tool.Devices.Add')"
        type="primary"
        size="small"
        icon="el-icon-plus"
        @click="openCreate"
        >Add Minew Node</el-button
      >
    </div>
    <el-table
      :data="filteredNodes"
      v-loading="loading"
      border
      class="app-table"
      :max-height="tableHeight"
    >
      <el-table-column prop="nodeId" label="Node ID" min-width="240" />
      <el-table-column
        prop="friendlyName"
        label="Friendly Name"
        min-width="190"
        show-overflow-tooltip
      >
        <template slot-scope="{ row }">{{ row.friendlyName || '--' }}</template>
      </el-table-column>
      <el-table-column prop="model" label="Model" min-width="180" />
      <el-table-column prop="status" label="Status" width="140"
        ><template slot-scope="{ row }"
          ><el-tag :type="statusType(row.status)" size="small">{{
            row.status
          }}</el-tag></template
        ></el-table-column
      >
      <el-table-column label="Actions" width="110" align="center"
        ><template slot-scope="{ row }"
          ><el-button
            v-if="$permission('Installation Tool.Devices.Edit')"
            type="danger"
            plain
            size="small"
            :disabled="row.status && row.status !== 'Unassigned'"
            @click="deleteNode(row)"
            >Delete</el-button
          ></template
        ></el-table-column
      >
    </el-table>
    <el-dialog
      title="Add Minew Node"
      :visible.sync="showCreate"
      width="500px"
      @close="resetForm"
    >
      <el-form :model="form" ref="nodeForm" label-width="130px">
        <el-form-item label="Node ID" required>
          <div class="hex-input-group">
            <span class="hex-prefix">AC:23:3F:C0:</span>
            <template v-for="(item, index) in nodeHexChars">
              <span
                v-if="index === 2"
                :key="'separator-' + index"
                class="hex-separator"
                >:</span
              >
              <input
                :key="index"
                :ref="'nH' + index"
                v-model="item.c"
                class="hex-char"
                maxlength="1"
                inputmode="text"
                autocomplete="off"
                @input="nHexIn(index)"
                @keydown="nHexDir(index, $event)"
              />
            </template>
          </div>
          <div v-if="nodeIdError" class="input-error">Required</div>
        </el-form-item>
        <el-form-item label="Model" prop="model"
          ><el-input v-model="form.model" placeholder="Optional"
        /></el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer"
        ><el-button class="save-btn" :loading="saving" @click="createNode"
          >ADD TO POOL</el-button
        ><el-button class="cancel-btn" @click="showCreate = false"
          >CANCEL</el-button
        ></span
      >
    </el-dialog>
  </div>
</template>

<script>
import LoraApiService from '@/services/LoraApiService'
import { loraError } from '@/utils/loraError'
import loraTableHeight from '@/mixins/loraTableHeight'

const emptyForm = () => ({ model: '' })
const emptyHexChars = () => Array.from({ length: 4 }, () => ({ c: '' }))
export default {
  name: 'LoraMinewNodePool',
  mixins: [loraTableHeight],
  data() {
    return {
      nodes: [],
      loading: false,
      saving: false,
      search: '',
      status: '',
      statuses: ['Unassigned', 'Assigned', 'Commissioned'],
      showCreate: false,
      form: emptyForm(),
      nodeHexChars: emptyHexChars(),
      nodeIdError: false
    }
  },
  computed: {
    nodeFullId() {
      const chars = this.nodeHexChars.map(item => item.c)
      return `AC:23:3F:C0:${chars.slice(0, 2).join('')}:${chars
        .slice(2)
        .join('')}`
    },
    filteredNodes() {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.nodes
      return this.nodes.filter(node =>
        [node.nodeId, node.friendlyName, node.model, node.status].some(value =>
          String(value || '')
            .toLowerCase()
            .includes(query)
        )
      )
    }
  },
  mounted() {
    this.loadNodes()
  },
  methods: {
    responseItems(response) {
      const body = response.data || []
      const items = body.data || body.items || body.Items || body
      return Array.isArray(items) ? items : []
    },
    async loadNodes() {
      this.loading = true
      try {
        const response = await LoraApiService.getNodes(
          this.status ? { status: this.status } : {}
        )
        this.nodes = this.responseItems(response)
      } catch (error) {
        this.$message.error(loraError(error, 'Failed to load Minew Node pool'))
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.form = emptyForm()
      this.nodeHexChars = emptyHexChars()
      this.nodeIdError = false
      this.showCreate = true
      this.$nextTick(() => this.focusHex('nH', 0))
    },
    resetForm() {
      this.$refs.nodeForm && this.$refs.nodeForm.resetFields()
      this.nodeHexChars = emptyHexChars()
      this.nodeIdError = false
    },
    async createNode() {
      if (this.nodeHexChars.some(item => !item.c)) {
        this.nodeIdError = true
        this.focusHex('nH', this.nodeHexChars.findIndex(item => !item.c))
        return
      }
      this.saving = true
      try {
        await LoraApiService.createNode({
          nodeId: this.nodeFullId,
          model: this.form.model || undefined
        })
        this.$message.success('Minew Node added to pool')
        this.showCreate = false
        this.loadNodes()
      } catch (error) {
        this.$message.error(loraError(error, 'Failed to add Minew Node'))
      } finally {
        this.saving = false
      }
    },
    focusHex(prefix, index) {
      const ref = this.$refs[prefix + index]
      const input = Array.isArray(ref) ? ref[0] : ref
      input && input.focus()
    },
    nHexIn(index) {
      const value = this.nodeHexChars[index].c
        .replace(/[^0-9a-f]/gi, '')
        .toUpperCase()
        .slice(-1)
      this.nodeHexChars[index].c = value
      this.nodeIdError = false
      if (value && index < 3) this.focusHex('nH', index + 1)
    },
    nHexDir(index, event) {
      if (
        event.key === 'Backspace' &&
        !this.nodeHexChars[index].c &&
        index > 0
      ) {
        this.focusHex('nH', index - 1)
      } else if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        this.focusHex('nH', index - 1)
      } else if (event.key === 'ArrowRight' && index < 3) {
        event.preventDefault()
        this.focusHex('nH', index + 1)
      }
    },
    async deleteNode(node) {
      try {
        await this.$confirm(
          'Delete Minew Node ' + node.nodeId + ' from the pool?',
          'Confirm Delete',
          { type: 'warning', confirmButtonText: 'Delete' }
        )
      } catch {
        return
      }
      try {
        await LoraApiService.deleteNode(node.id)
        this.$message.success('Minew Node deleted')
        this.loadNodes()
      } catch (error) {
        this.$message.error(loraError(error, 'Failed to delete Minew Node'))
      }
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : '--'
    },
    statusType(status) {
      return (
        { Unassigned: 'info', Assigned: 'warning', Commissioned: 'success' }[
          status
        ] || 'info'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/varible.scss';
.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  .result-count {
    margin-left: auto;
    color: $font-color;
    font-size: 13px;
  }
}
.hex-input-group {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  &:focus-within {
    border-color: #409eff;
  }
}
.hex-prefix,
.hex-separator,
.hex-char {
  font-family: monospace;
  font-size: 14px;
}
.hex-prefix,
.hex-separator {
  color: #909399;
  user-select: none;
}
.hex-char {
  width: 20px;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #303133;
  font-weight: 600;
  text-align: center;
  text-transform: uppercase;
}
.input-error {
  position: absolute;
  color: #f56c6c;
  font-size: 12px;
  line-height: 1;
  padding-top: 4px;
}
</style>
